import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	RadioGroup,
	FormControlLabel,
	Radio,
	Typography,
} from "@mui/material";
import {
	createNotification,
	CustomButton,
	CustomDialog,
	CustomTextField,
	CustomIconButton,
	CustomTooltip,
	icon,
	IconComponent,
	KnowledgeBasePopOver,
} from "../../../../../../common/components";
import { translate, SegFlags } from "../../../../../../common/providers";
import styles from "./ProjectCreationModal.module.css";
import { ApiService, ProjectCreationService } from "../../../../../../api";
import { useApi, useTrackEvent } from "../../../../../../common/hooks";
import {
	setHasCategorySegFlag,
	setHasPermissionsSegFlag,
	setHasProjectRequirementsSegFlag,
	setHasTeamSegFlag,
	setIsModified,
	setIsNewProjCreated,
	setProjectCreation,
	setCheckedCategoriesIds,
} from "../../../../../new-project/slice/newProjectSlice";
import { PackageSelection } from "./package-selection";
import { NavUtils } from "../../../../../../navigation";

const DEFAULT_PROJECT_TYPE = "DEFAULT";

const ProjectCreationModal = ({ open, onClose }) => {
	const [openCancellationDialog, setOpenCancellationDialog] = useState(false);
	const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
	const [createdProjectId, setCreatedProjectId] = useState(null);
	const [selectedPackage, setSelectedPackage] = useState(null);
	const projectCreation = useSelector(({ newProject }) => newProject.projectCreation);
	const hasProjectRequirementsSegFlag = useSelector(({ newProject }) => newProject.hasProjectRequirementsSegFlag);
	const companyName = useSelector(({ context }) => context.company?.name || "");
	const cancelTokenSourceRef = useRef(null);
	const radioGroupRef = useRef(null);
	const projectCreationRef = useRef(projectCreation);
	const trackEvent = useTrackEvent();
	projectCreationRef.current = projectCreation;
	const { call: validateProject } = useApi(ProjectCreationService.validateProject);
	const { call: abortProjectCreation } = useApi(ProjectCreationService.abortProjectCreation);
	const { call: updateProjectDetails } = useApi(ProjectCreationService.updateProjectDetails);

	const dispatch = useDispatch();

	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			if (cancelTokenSourceRef.current) {
				ApiService.cancelTokens(cancelTokenSourceRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (open && !cancelTokenSourceRef.current) {
			cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		}
	}, [open]);

	useEffect(() => {
		if (!open || !selectedPackage?.id) {
			return;
		}
		const packageId = selectedPackage.id;
		if (!cancelTokenSourceRef.current) {
			return;
		}
		ProjectCreationService.getIsFeatureInPackage(
			{ packageId },
			{
				segFlags: [SegFlags.PROJECT_REQUIREMENTS, SegFlags.CATEGORY, SegFlags.TEAM, SegFlags.PERMISSIONS],
			},
			cancelTokenSourceRef.current.token
		)
			.then((data) => {
				const localHasProjectReqSegFlag = data.find(
					(d) => d.segmentationFlag === SegFlags.PROJECT_REQUIREMENTS
				).isActive;
				const localHasCategorySegFlag = data.find((d) => d.segmentationFlag === SegFlags.CATEGORY).isActive;
				const localHasTeamSegFlag = data.find((d) => d.segmentationFlag === SegFlags.TEAM).isActive;
				const localHasPermissionsSegFlag = data.find(
					(d) => d.segmentationFlag === SegFlags.PERMISSIONS
				).isActive;
				dispatch(setHasProjectRequirementsSegFlag(localHasProjectReqSegFlag));
				dispatch(setHasCategorySegFlag(localHasCategorySegFlag));
				dispatch(setHasTeamSegFlag(localHasTeamSegFlag));
				dispatch(setHasPermissionsSegFlag(localHasPermissionsSegFlag));
				if (!localHasProjectReqSegFlag) {
					const latest = projectCreationRef.current;
					dispatch(
						setProjectCreation({
							...latest,
							project: { ...latest.project, reqDetection: false },
						})
					);
				}
				if (!localHasCategorySegFlag) {
					dispatch(setCheckedCategoriesIds([]));
				}
			})
			.catch((err) => {
				console.error("Error fetching package features:", err);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, selectedPackage, open]);

	const handleCancellationDialog = () => setOpenCancellationDialog((prev) => !prev);

	const handleValidateProject = () => {
		validateProject({ projectId: projectCreation.project.id })
			.then((data) => {
				if (data?.id) {
					dispatch(setIsNewProjCreated(true));
					setCreatedProjectId(data?.id);
					setOpenConfirmationDialog(true);
					trackEvent("Project_Validated_Creation");
					createNotification({
						type: "success",
						message: translate("dashboards.new-project.confirmation-dialog.title"),
					});
					onClose();
				}
			})
			.catch((err) => console.error(err));
	};

	const handleCloseConfirmationDialog = () => {
		setOpenConfirmationDialog(false);
		window.location.href = NavUtils.goToDocumentCenter(createdProjectId);
	};

	const updateProjectDetailsAndValidate = () => {
		updateProjectDetails(
			{ projectId: projectCreation.project.id },
			{
				name: projectCreation.project.name,
				type: DEFAULT_PROJECT_TYPE,
				packageId: selectedPackage?.id,
				reqDetection: projectCreation.project?.reqDetection || false,
			}
		)
			.then(() => {
				handleValidateProject();
			})
			.catch((err) => {
				console.error(err);
			});
	};

	const handleAbortProjectCreation = () => {
		const projectId = projectCreation?.project?.id;
		handleCancellationDialog();
		if (!projectId) {
			onClose();
			return;
		}
		abortProjectCreation({ projectId })
			.then(() => {
				dispatch(setProjectCreation({}));
				createNotification({
					type: "success",
					message: translate("dashboards.new-project.success-message.project-abort"),
				});
				onClose();
			})
			.catch((err) => {
				console.error(err);
			});
	};

	const handleChangeProjectName = (e) => {
		dispatch(
			setProjectCreation({
				...projectCreation,
				project: { ...projectCreation.project, name: e?.target.value || "" },
			})
		);
		dispatch(setIsModified(true));
	};

	const handleSelectPackage = React.useCallback(
		(pkg) => {
			setSelectedPackage(pkg);
			const { current } = projectCreationRef;
			dispatch(
				setProjectCreation({
					...current,
					project: {
						...current.project,
						package: pkg || "",
						type: DEFAULT_PROJECT_TYPE,
					},
					featPackage: pkg?.name,
				})
			);
			dispatch(setIsModified(true));
		},
		[dispatch]
	);

	const handleChangeReqDetection = (e) => {
		const newValue = Number.parseInt(e.target.value, 10) === 1;
		dispatch(
			setProjectCreation({
				...projectCreation,
				project: { ...projectCreation.project, reqDetection: newValue },
			})
		);
		dispatch(setIsModified(true));
	};

	const isCreationButtonDisabled = () => !projectCreation.project?.name || !selectedPackage?.id;

	if (!open && !openConfirmationDialog) {
		return null;
	}

	return (
		<>
			<Dialog
				fullWidth
				classes={{ paper: styles.dialogPaper }}
				maxWidth="md"
				open={open}
				onClose={handleCancellationDialog}
			>
				<DialogTitle className={styles.dialogTitle}>
					{translate("dashboards.dash-portfolio.button.new-project")}
					<CustomIconButton
						btnClassName={styles.closeButton}
						icon={icon.faTimes}
						onClick={handleCancellationDialog}
					/>
				</DialogTitle>
				<DialogContent className={styles.dialogContent}>
					<div className={styles.fieldSection}>
						<div className={styles.fieldLabel}>
							{translate("dashboards.new-project.details-and-documents.text-field-label.project-name")}
							<IconComponent
								className={styles.requiredIcon}
								color="var(--red-500)"
								icon={icon.faAsterisk}
								size="2xs"
							/>
						</div>
						<CustomTextField
							autoFocus
							fullWidth
							className={styles.textField}
							data-testid="new-project.name"
							placeholder={translate(
								"dashboards.new-project.details-and-documents.text-field.project-name.placeholder"
							)}
							size="small"
							value={projectCreation?.project?.name || ""}
							variant="outlined"
							onChange={handleChangeProjectName}
						/>
					</div>
					<PackageSelection selectedPackage={selectedPackage} onSelectPackage={handleSelectPackage} />
					{hasProjectRequirementsSegFlag && (
						<div className={styles.reqSection}>
							<div className={styles.title}>
								{translate(
									"dashboards.new-project.details-and-documents.implicit-requirements.algorithm.selection"
								)}
								<IconComponent
									className={styles.requiredIcon}
									color="var(--red-500)"
									icon={icon.faAsterisk}
									size="2xs"
								/>
								<KnowledgeBasePopOver
									label={translate(
										"dashboards.new-project.details-and-documents.implicit-requirements.algorithm.information"
									)}
									link={translate(
										"dashboards.new-project.details-and-documents.implicit-requirements.algorithm.link"
									)}
								/>
							</div>
							<RadioGroup
								ref={radioGroupRef}
								data-testid="new-project.detection"
								value={(projectCreation.project?.reqDetection && 1) || 0}
								onChange={handleChangeReqDetection}
							>
								<FormControlLabel
									control={<Radio color="primary" size="small" />}
									label={
										<Typography className={styles.radio}>
											{translate(
												"dashboards.new-project.details-and-documents.implicit-requirements.algorithm.none"
											)}
										</Typography>
									}
									value={0}
								/>
								<div className={styles.package}>
									<FormControlLabel
										control={<Radio color="primary" size="small" />}
										label={
											<Typography className={styles.radio}>
												{translate(
													"dashboards.new-project.details-and-documents.implicit-requirements.algorithm.standard"
												)}
											</Typography>
										}
										value={1}
									/>
									<div className={styles.tooltipReq} />
								</div>
								<FormControlLabel
									disabled
									control={<Radio color="primary" size="small" />}
									label={
										<CustomTooltip
											title={translate(
												"dashboards.new-project.details-and-documents.implicit-requirements.no-alorightm-warning"
											)}
										>
											<Typography className={`${styles.radio} ${styles["radio--disabled"]}`}>
												{translate(
													"dashboards.new-project.details-and-documents.implicit-requirements.algorithm.proprietary",
													{ companyName }
												)}
											</Typography>
										</CustomTooltip>
									}
									value={2}
								/>
							</RadioGroup>
						</div>
					)}
				</DialogContent>
				<DialogActions className={styles.dialogActions}>
					<CustomButton color="primary" variant="outlined" onClick={handleCancellationDialog}>
						{translate("common:btn.cancel")}
					</CustomButton>
					<CustomButton
						color="primary"
						data-testid="new-project.submit"
						disabled={isCreationButtonDisabled()}
						variant="contained"
						onClick={updateProjectDetailsAndValidate}
					>
						{translate("common:btn.submit")}
					</CustomButton>
				</DialogActions>
			</Dialog>
			<CustomDialog
				closeLabel={translate("dashboards.new-project.cancellation-dialog.button.continue-edition")}
				iconColor="var(--red-500)"
				iconModel={icon.faExclamationTriangle}
				open={openCancellationDialog}
				submitColor="destructive"
				submitLabel={translate("dashboards.new-project.cancellation-dialog.button.abort")}
				subTitle={translate("dashboards.new-project.cancellation-dialog.content")}
				title={translate("dashboards.new-project.cancellation-dialog.title")}
				onClose={handleCancellationDialog}
				onSubmit={handleAbortProjectCreation}
			/>
			<CustomDialog
				iconColor="var(--shades-grey-v2-30)"
				iconModel={icon.faCheckCircle}
				open={openConfirmationDialog}
				submitLabel={translate("dashboards.new-project.confirmation-dialog.button-text")}
				subTitle={translate("dashboards.new-project.confirmation-dialog.content")}
				title={translate("dashboards.new-project.confirmation-dialog.title")}
				onSubmit={handleCloseConfirmationDialog}
			/>
		</>
	);
};

export default ProjectCreationModal;
